/**
 * 共通ローディングコンポーネント
 *
 * @file Loading.tsx
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * アプリケーション全体で使用するローディングUIコンポーネント。
 * スピナー、プログレスバー、スケルトンなど、様々なローディング状態を表現。
 */
import { Box, CircularProgress, LinearProgress, Typography, Skeleton, Card, CardContent,  } from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { SxProps, Theme } from '@mui/material/styles';
import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

// ローディングの種類
type LoadingVariant = 'circular' | 'linear' | 'skeleton' | 'page' | 'overlay';

export interface LoadingProps {
  variant?: LoadingVariant;
  size?: 'small' | 'medium' | 'large';
  message?: string;
  fullScreen?: boolean;
  overlay?: boolean;
  skeletonCount?: number;
  sx?: SxProps<Theme>;
}

// 円形プログレス
const CircularLoading: React.FC<{ size: string; message?: string }> = ({ size, message }) => {
  const sizeMap = { small: 24, medium: 40, large: 56 };
  
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={2}>
      <CircularProgress
        size={sizeMap[size as keyof typeof sizeMap]}
        thickness={4}
        sx={{
          color: 'primary.main',
          animationDuration: '1.4s',
        }}
      />
      {message && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {message}
        </Typography>
      )}
    </Box>
  );
};

// リニアプログレス
const LinearLoading: React.FC<{ message?: string }> = ({ message }) => (
  <Box width="100%">
    {message && (
      <Typography variant="body2" color="text.secondary" mb={1}>
        {message}
      </Typography>
    )}
    <LinearProgress
      sx={{
        height: 4,
        borderRadius: 2,
        backgroundColor: 'grey.200',
        '& .MuiLinearProgress-bar': {
          borderRadius: 2,
        },
      }}
    />
  </Box>
);

// スケルトンローディング
const SkeletonLoading: React.FC<{ count: number; variant?: 'table' | 'card' | 'list' }> = ({ 
  count, 
  variant = 'card' 
}) => {
  const renderTableSkeleton = () => (
    <Box>
      <Skeleton variant="rectangular" height={48} sx={{ mb: 2 }} />
      {Array.from({ length: count }).map((_, index) => (
        <Box key={index} sx={{ mb: 1 }}>
          <Skeleton variant="rectangular" height={40} />
        </Box>
      ))}
    </Box>
  );

  const renderCardSkeleton = () => (
    <Grid container spacing={2}>
      {Array.from({ length: count }).map((_, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <Card>
            <CardContent>
              <Skeleton variant="rectangular" height={120} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="60%" />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderListSkeleton = () => (
    <Box>
      {Array.from({ length: count }).map((_, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="40%" />
          </Box>
        </Box>
      ))}
    </Box>
  );

  switch (variant) {
    case 'table':
      return renderTableSkeleton();
    case 'list':
      return renderListSkeleton();
    default:
      return renderCardSkeleton();
  }
};

// ページ全体のローディング
const PageLoading: React.FC<{ message?: string }> = ({ message }) => {
  const { t } = useLanguage();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(var(--app-height) - 64px)',
        backgroundColor: 'background.default',
        gap: 3,
      }}>
      <CircularProgress size={60} thickness={4} />
      <Typography variant="h6" color="text.secondary">
        {message || t('status.loading')}
      </Typography>
    </Box>
  );
};

// オーバーレイローディング
const OverlayLoading: React.FC<{ message?: string }> = ({ message }) => {
  const { t } = useLanguage();

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'var(--bg-transparent-black)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 'modal',
        gap: 2,
      }}>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 2,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          minWidth: 200,
          boxShadow: 3,
        }}>
        <CircularProgress size={48} thickness={4} />
        <Typography variant="body1" color="text.primary" textAlign="center">
          {message || t('status.loading')}
        </Typography>
      </Box>
    </Box>
  );
};

// メインコンポーネント
const Loading: React.FC<LoadingProps> = ({
  variant = 'circular',
  size = 'medium',
  message,
  fullScreen = false,
  overlay = false,
  skeletonCount = 3,
  sx,
}) => {
  const containerSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...(!fullScreen && { minHeight: 120 }),
    ...(fullScreen && { minHeight: 'calc(var(--app-height) - 64px)' }),
    ...sx,
  };

  // オーバーレイまたはフルスクリーンの場合
  if (overlay) {
    return <OverlayLoading message={message} />;
  }

  if (fullScreen) {
    return <PageLoading message={message} />;
  }

  // 通常のローディング
  return (
    <Box sx={containerSx}>
      {variant === 'circular' && <CircularLoading size={size} message={message} />}
      {variant === 'linear' && <LinearLoading message={message} />}
      {variant === 'skeleton' && <SkeletonLoading count={skeletonCount} />}
      {variant === 'page' && <PageLoading message={message} />}
    </Box>
  );
};

// 個別エクスポート用コンポーネント
export { CircularLoading, LinearLoading, SkeletonLoading, PageLoading, OverlayLoading };

// プリセット
export const LoadingPresets = {
  tableLoading: (props?: Partial<LoadingProps>) => (
    <Loading variant="skeleton" skeletonCount={5} {...props} />
  ),
  cardLoading: (props?: Partial<LoadingProps>) => (
    <Loading variant="skeleton" skeletonCount={3} {...props} />
  ),
  pageLoading: (props?: Partial<LoadingProps>) => (
    <Loading variant="page" {...props} />
  ),
  buttonLoading: (props?: Partial<LoadingProps>) => (
    <Loading variant="circular" size="small" {...props} />
  ),
  overlayLoading: (props?: Partial<LoadingProps>) => (
    <Loading overlay {...props} />
  ),
};

// カスタムフック
export const useLoading = () => {
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState<string>('');

  const startLoading = (loadingMessage?: string) => {
    setMessage(loadingMessage || '');
    setLoading(true);
  };

  const stopLoading = () => {
    setLoading(false);
    setMessage('');
  };

  return {
    loading,
    message,
    startLoading,
    stopLoading,
    LoadingComponent: loading ? (
      <Loading variant="overlay" message={message} />
    ) : null,
  };
};

export default Loading;
